/**
 * cache/space.ts — embed a mission graph into an integer cell grid.
 *
 * The second of Dormans' two steps. Unexplored's trick is PROGRESSIVE REFINEMENT: lay
 * the graph out on a small grid, then expand it, inserting corridor space between the
 * nodes, then expand again to tile resolution. We take the first two rungs:
 *
 *   GRAPH GRID   5×5 (tier 0) / 7×7 (tier 1) — one cell per room
 *        │  ×2 − 1
 *   WORKING GRID 9×9 / 13×13 — rooms on EVEN cells, corridors on the odd cells between
 *
 * The third rung (tilemap resolution) is the dressing layer's job and is theme-specific,
 * so it does not live in shared.
 *
 * The loop is placed as a RING, which is the whole reason the layouts read as designed:
 * you walk out one way and come back another, and the goal sits in the middle of the
 * space you have been circling. Off-loop nodes hang off their anchor.
 *
 * Integer math only — no trig anywhere (the pmath-ban gate covers this directory).
 * Failure returns null; the caller rerolls with the next sub-seed rather than trying to
 * repair a bad placement.
 */
import type { Rng } from "../rng.js";
import { rangeInt } from "../rng.js";
import type { MissionGraph } from "./mission.js";
import type { CacheEdge, CacheEdgeKind, CacheRole, CacheRoom } from "./types.js";

/** Graph-grid size per tier (cells per axis). */
const GRAPH_GRID = [5, 7] as const;
/** Ring radius the loop is placed on, per tier. */
const RING_RADIUS = [1, 2] as const;
/**
 * Metres per working-grid cell, per tier.
 *
 * ⚠ THE CURVATURE BUDGET IS WHAT BOUNDS THIS, NOT TASTE. A layout's half-extent is
 * `((W−1)/2)·cell + maxRoomHalf`, and the pad sphere drops `d²/2R` below the tangent
 * plane at that distance — the cache render gate asserts ≤ 0.15 m at
 * `CACHE_PAD_RADIUS` = 60 km. Raised 12/14 → 18/20 (+50%) for navigation feel after
 * live play read the corridors and rooms as cramped:
 *
 *   tier 0: W = 9,  half-extent 4·18 +  9 =  81 m → drop  81²/120000 = 0.055 m
 *   tier 1: W = 13, half-extent 6·20 + 10 = 130 m → drop 130²/120000 = 0.141 m
 *
 * 0.141 is INSIDE the 0.15 budget but no longer comfortably — a further widening needs
 * `CACHE_PAD_RADIUS` raised with it, not this constant nudged alone.
 */
const CELL_M = [18, 20] as const;

/** Footprint as a fraction of one working cell, per role. Arenas want room to fight. */
const ROLE_SCALE: Record<CacheRole, number> = {
  entrance: 0.70,
  transit: 0.65,
  arena: 0.95,
  gate: 0.60,
  key: 0.70,
  treasure: 0.75,
  boss: 1.00,
  shortcut: 0.60,
  vista: 0.80,
};

/** Dressing tags per role — the pool the theme layer keys off. */
const ROLE_ARCHETYPES: Record<CacheRole, readonly string[]> = {
  entrance: ["mouth", "threshold"],
  transit: ["corridor", "gallery", "stair", "bend"],
  arena: ["hall", "yard", "pit"],
  gate: ["seal", "portcullis"],
  key: ["shrine", "cache", "console"],
  treasure: ["hoard", "reliquary"],
  boss: ["throne", "crucible"],
  shortcut: ["chute", "ladder"],
  vista: ["balcony", "overlook"],
};

export interface SpaceResult {
  rooms: CacheRoom[];
  edges: CacheEdge[];
  /** Working-grid size. */
  grid: number;
  /** Metres per working-grid cell. */
  cell: number;
  /** Pad-local half-extent, metres. */
  extent: number;
}

const idx = (cu: number, cv: number, g: number): number => cv * g + cu;

/**
 * Cells of the square ring at Chebyshev radius `r` about the grid centre, walked
 * clockwise from the top-left corner. 8r cells — the loop's seats.
 */
function ringCells(c: number, r: number): [number, number][] {
  const out: [number, number][] = [];
  for (let u = c - r; u < c + r; u++) out.push([u, c - r]);       // top, →
  for (let v = c - r; v < c + r; v++) out.push([c + r, v]);       // right, ↓
  for (let u = c + r; u > c - r; u--) out.push([u, c + r]);       // bottom, ←
  for (let v = c + r; v > c - r; v--) out.push([c - r, v]);       // left, ↑
  return out;
}

/** 4-neighbours then diagonals — the search order for hanging an off-loop node. */
const NEIGHBOURS: readonly [number, number][] = [
  [1, 0], [0, 1], [-1, 0], [0, -1],
  [1, 1], [-1, 1], [-1, -1], [1, -1],
];

/**
 * Place the mission on the grid. Returns null when a node or a corridor cannot be
 * seated — the caller rerolls.
 */
export function embedMission(m: MissionGraph, rng: Rng, diag?: string[]): SpaceResult | null {
  const tier = Math.min(1, Math.max(0, m.tier));
  const G = GRAPH_GRID[tier];
  const r = RING_RADIUS[tier];
  const cellM = CELL_M[tier];
  const c = (G - 1) / 2;
  const W = 2 * G - 1;

  const ring = ringCells(c, r);
  if (m.loop.length > ring.length) { diag?.push(`loop ${m.loop.length} > ring ${ring.length}`); return null; }

  /** graph cell → node index, or -1. */
  const owner = new Int32Array(G * G).fill(-1);
  /** node index → graph cell. */
  const at: ([number, number] | null)[] = new Array(m.nodes.length).fill(null);

  // The ENTIRE ring is reserved, not just the seats a loop node lands on. A loop with
  // fewer nodes than seats leaves gaps, and the loop's own corridors run along the ring
  // THROUGH those gaps — so an off-loop node allowed to squat in one would be cut
  // straight through by the corridor beside it. Reserving the ring makes that
  // impossible by construction instead of by rejection.
  const reserved = new Uint8Array(G * G);
  for (const [cu, cv] of ring) reserved[idx(cu, cv, G)] = 1;

  // ── loop: spread the ring seats evenly so the walk feels balanced ──────────────
  for (let k = 0; k < m.loop.length; k++) {
    const seat = Math.floor((k * ring.length) / m.loop.length);
    const [cu, cv] = ring[seat];
    if (owner[idx(cu, cv, G)] !== -1) { diag?.push(`loop seat clash at ${cu},${cv}`); return null; }
    owner[idx(cu, cv, G)] = m.loop[k];
    at[m.loop[k]] = [cu, cv];
  }

  /** Chebyshev radius from the grid centre — the ring sits at exactly `r`. */
  const rad = (cu: number, cv: number): number => Math.max(Math.abs(cu - c), Math.abs(cv - c));

  // ── off-loop: hang each node on a free cell beside its anchor ──────────────────
  // Preference is OUTWARD (increasing radius). The ring interior is tiny — at tier 0 it
  // is a single cell — so an inward-first policy dead-ends the moment a stub is more
  // than one room long (J → gate → boss), which is most of the library. Outward opens
  // onto the whole border.
  const spin = rangeInt(rng, 0, NEIGHBOURS.length - 1); // deterministic rotation
  for (const i of m.offLoop) {
    const anchor = m.nodes[i].anchor;
    const seat = anchor >= 0 ? at[anchor] : at[m.entrance];
    if (!seat) { diag?.push(`node ${i} anchored to an unplaced node`); return null; }

    // Search distance 1 first, then distance 2. Sticking to immediate neighbours
    // starves long stub chains (J → gate → gate → boss): the interior beside the ring
    // is small and the border fills up with bypass rooms, so the chain dead-ends with
    // free cells still available two steps away. A distance-2 seat just means a longer
    // corridor, which the route search below handles.
    const candidates: { cu: number; cv: number; dist: number; rank: number; tie: number }[] = [];
    for (let dist = 1; dist <= 2 && candidates.length === 0; dist++) {
      for (let du = -dist; du <= dist; du++) {
        for (let dv = -dist; dv <= dist; dv++) {
          if (Math.max(Math.abs(du), Math.abs(dv)) !== dist) continue; // ring at exactly `dist`
          const cu = seat[0] + du, cv = seat[1] + dv;
          if (cu < 0 || cv < 0 || cu >= G || cv >= G) continue;
          if (reserved[idx(cu, cv, G)] === 1 || owner[idx(cu, cv, G)] !== -1) continue;
          // Deterministic per-attempt shuffle: rotate the tie-break by `spin`.
          const tie = ((du + dist) * (2 * dist + 1) + (dv + dist) + spin) % 64;
          candidates.push({ cu, cv, dist, rank: -rad(cu, cv), tie });
        }
      }
    }
    candidates.sort((p, q) => (p.dist - q.dist) || (p.rank - q.rank) || (p.tie - q.tie));
    const spot = candidates[0];
    if (spot === undefined) { diag?.push(`no free cell beside anchor for node ${i} (${m.nodes[i].role})`); return null; }
    owner[idx(spot.cu, spot.cv, G)] = i;
    at[i] = [spot.cu, spot.cv];
  }

  // ── rooms, in mission-node order (index i === array position) ──────────────────
  const half = (W - 1) / 2;
  const rooms: CacheRoom[] = [];
  let maxHalf = 0;
  for (const n of m.nodes) {
    const seat = at[n.i];
    if (!seat) return null;
    const wu = seat[0] * 2, wv = seat[1] * 2; // graph cell → working cell
    const size = cellM * ROLE_SCALE[n.role];
    maxHalf = Math.max(maxHalf, size * 0.5);
    rooms.push({
      i: n.i,
      role: n.role,
      archetype: ROLE_ARCHETYPES[n.role][rangeInt(rng, 0, ROLE_ARCHETYPES[n.role].length - 1)],
      cu: wu, cv: wv,
      x: (wu - half) * cellM,
      z: (wv - half) * cellM,
      w: size, d: size,
      // Placeholder. The terrace ladder is THEME-DEPENDENT (its step and jitter are
      // per-theme constants) and this layer is deliberately theme-blind, so
      // `generateCache` fills it in — see `roomFloorHeights` in layout.ts.
      floorH: 0,
    });
  }

  /** Working cells occupied by a room — corridors may not pass through these. */
  const roomCell = new Set<number>(rooms.map((rm) => idx(rm.cu, rm.cv, W)));

  // ── corridors: an L-route per edge, forbidden from crossing a third room ───────
  // A corridor cutting through an unrelated room would silently add a connection —
  // and a corridor through a GATE room would let you skip the gate entirely, which
  // is a design bug the reachability check cannot see (extra connectivity only ever
  // makes things MORE reachable). So a crossing fails the whole placement.
  //
  // …AND THE SAME HOLE EXISTS BETWEEN TWO CORRIDORS. A plain corridor that shares a
  // working cell with a GATED corridor is a doorway onto the far side of the seal: you
  // walk the plain corridor, step off at the shared cell, and continue past a barrier
  // you never opened. The key means nothing again, and reachability still cannot see
  // it. (This is the `TODO(P5)` all three dressing layers carry — geometry alone can
  // never close it, because the two corridors genuinely do meet on the grid.) So a
  // placement where any corridor intersects a GATED corridor's cells is rejected and
  // the caller rerolls.
  //
  // Only GATED crossings are rejected. Plain × plain crossings stay legal and are part
  // of the loop charm — an unlocked shortcut you discover by walking is exactly what
  // the cyclic layouts are for.
  const isGated = (e: { kind: CacheEdgeKind; lock: number }): boolean =>
    e.kind === "gate" || e.lock >= 0;

  // Gated edges route FIRST so the plain ones can be routed AROUND them (each edge has
  // four candidate shapes; whichever pass runs second gets to detour). Within pass 1
  // the cells accumulate as they are claimed, so two gated corridors cannot cross each
  // other either — that would be a hole in BOTH locks.
  const order: number[] = [];
  for (let ei = 0; ei < m.edges.length; ei++) if (isGated(m.edges[ei])) order.push(ei);
  for (let ei = 0; ei < m.edges.length; ei++) if (!isGated(m.edges[ei])) order.push(ei);

  /** Working cells already claimed by a GATED corridor — forbidden to everyone else. */
  const gatedCell = new Set<number>();
  // Emitted in MISSION EDGE ORDER regardless of routing order: `edges[i]` must keep
  // lining up with `m.edges[i]` for every downstream consumer (and the server's
  // gate-progression state indexes this array).
  const edges: CacheEdge[] = new Array(m.edges.length);
  for (const ei of order) {
    const e = m.edges[ei];
    const A = rooms[e.a], B = rooms[e.b];
    const clean = (path: [number, number][]): boolean =>
      path.every(([u, v]) => {
        const c = idx(u, v, W);
        return !roomCell.has(c) && !gatedCell.has(c);
      });
    const chosen = routeCandidates(A.cu, A.cv, B.cu, B.cv, W).find(clean) ?? null;
    if (chosen === null) {
      diag?.push(`corridor ${e.a}→${e.b} crosses a room or a gated corridor`);
      return null;
    }
    edges[ei] = { a: e.a, b: e.b, kind: e.kind, lock: e.lock, cells: chosen };
    if (isGated(e)) for (const [u, v] of chosen) gatedCell.add(idx(u, v, W));
  }

  return { rooms, edges, grid: W, cell: cellM, extent: half * cellM + maxHalf };
}

/**
 * Interior cells of an axis-aligned route through `waypoints` (endpoints excluded).
 * Each leg moves along one axis at a time, so the result is a staircase of cells.
 */
function routeVia(waypoints: readonly [number, number][]): [number, number][] {
  const [au, av] = waypoints[0];
  const [bu, bv] = waypoints[waypoints.length - 1];
  const out: [number, number][] = [];
  const seen = new Set<number>();
  const push = (u: number, v: number): void => {
    if ((u === au && v === av) || (u === bu && v === bv)) return;
    const key = u * 4096 + v;
    if (seen.has(key)) return;
    seen.add(key);
    out.push([u, v]);
  };
  for (let k = 0; k + 1 < waypoints.length; k++) {
    const [pu, pv] = waypoints[k];
    const [qu, qv] = waypoints[k + 1];
    const du = qu === pu ? 0 : qu > pu ? 1 : -1;
    for (let u = pu; u !== qu; u += du) push(u, pv);
    const dv = qv === pv ? 0 : qv > pv ? 1 : -1;
    for (let v = pv; v !== qv; v += dv) push(qu, v);
    push(qu, qv);
  }
  return out;
}

/**
 * Route options between two working cells, cheapest shape first: the two L-bends, then
 * two Z-bends that turn at the midpoint. Offering alternatives matters — with only the
 * two L's, a single unlucky room sitting on both corners throws away an entire
 * placement that a one-cell detour would have saved.
 */
function routeCandidates(au: number, av: number, bu: number, bv: number, W: number): [number, number][][] {
  const clampU = (u: number): number => Math.min(W - 1, Math.max(0, u));
  const mu = clampU((au + bu) >> 1), mv = clampU((av + bv) >> 1);
  return [
    routeVia([[au, av], [bu, av], [bu, bv]]),          // L: along u, then v
    routeVia([[au, av], [au, bv], [bu, bv]]),          // L: along v, then u
    routeVia([[au, av], [mu, av], [mu, bv], [bu, bv]]), // Z: turn at the u midpoint
    routeVia([[au, av], [au, mv], [bu, mv], [bu, bv]]), // Z: turn at the v midpoint
  ];
}

/**
 * Flood-fill the working grid from the entrance over rooms + corridor cells, ignoring
 * locks (this is a GEOMETRIC check — can a body physically walk there — not a mission
 * check). Returns the set of reachable room indices.
 */
export function reachableRooms(space: SpaceResult, from: number): Set<number> {
  const W = space.grid;
  const open = new Set<number>();
  for (const rm of space.rooms) open.add(idx(rm.cu, rm.cv, W));
  for (const e of space.edges) for (const [u, v] of e.cells) open.add(idx(u, v, W));

  const roomAt = new Map<number, number>();
  for (const rm of space.rooms) roomAt.set(idx(rm.cu, rm.cv, W), rm.i);

  const start = space.rooms[from];
  const seen = new Set<number>([idx(start.cu, start.cv, W)]);
  const queue: [number, number][] = [[start.cu, start.cv]];
  const out = new Set<number>();
  while (queue.length > 0) {
    const [u, v] = queue.pop()!;
    const here = idx(u, v, W);
    const rid = roomAt.get(here);
    if (rid !== undefined) out.add(rid);
    for (const [du, dv] of NEIGHBOURS.slice(0, 4)) {
      const nu = u + du, nv = v + dv;
      if (nu < 0 || nv < 0 || nu >= W || nv >= W) continue;
      const key = idx(nu, nv, W);
      if (!open.has(key) || seen.has(key)) continue;
      seen.add(key);
      queue.push([nu, nv]);
    }
  }
  return out;
}
